import { useEffect, useRef } from 'react';

interface AnalyticsEvent {
  type: 'click' | 'scroll' | 'time' | 'interaction' | 'mouse' | 'page';
  timestamp: string;
  data: any;
  description?: string;
  color?: string;
  icon?: string;
}

interface AnalyticsData {
  sessionId: string;
  startTime: string;
  events: AnalyticsEvent[];
  pageViews: string[];
  totalTime: number;
  clicks: number;
  scrollDepth: number;
  interactions: number;
}

export const useAnalytics = () => {
  const analyticsData = useRef<AnalyticsData>({
    sessionId: generateSessionId(),
    startTime: new Date().toISOString(),
    events: [],
    pageViews: [window.location.pathname],
    totalTime: 0,
    clicks: 0,
    scrollDepth: 0,
    interactions: 0,
  });

  const startTimeRef = useRef(Date.now());
  const lastScrollRef = useRef(0);
  const mousePositions = useRef<Array<{ x: number; y: number; time: number }>>([]);

  useEffect(() => {
    // Track clicks
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const elementInfo = {
        tag: target.tagName,
        id: target.id,
        class: target.className,
        text: target.textContent?.substring(0, 50),
        x: e.clientX,
        y: e.clientY,
      };

      // Generate description
      let description = 'Clic en ';
      if (target.textContent && target.textContent.trim()) {
        description += `"${target.textContent.trim().substring(0, 30)}"`;
      } else if (target.id) {
        description += `elemento #${target.id}`;
      } else if (target.className) {
        description += `${target.tagName.toLowerCase()}`;
      } else {
        description += `posición (${e.clientX}, ${e.clientY})`;
      }

      analyticsData.current.clicks++;
      analyticsData.current.events.push({
        type: 'click',
        timestamp: new Date().toISOString(),
        data: elementInfo,
        description,
        color: 'cyan',
        icon: 'ri-cursor-line',
      });

      saveToLocalStorage();
    };

    // Track scroll
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrollPercent = Math.round((scrollTop / scrollHeight) * 100);

      if (scrollPercent > analyticsData.current.scrollDepth) {
        analyticsData.current.scrollDepth = scrollPercent;
      }

      // Track scroll events every 25%
      if (scrollPercent >= lastScrollRef.current + 25) {
        lastScrollRef.current = Math.floor(scrollPercent / 25) * 25;
        analyticsData.current.events.push({
          type: 'scroll',
          timestamp: new Date().toISOString(),
          data: { depth: scrollPercent },
          description: `Scroll alcanzó ${scrollPercent}%`,
          color: 'green',
          icon: 'ri-arrow-down-line',
        });
        saveToLocalStorage();
      }
    };

    // Track mouse movement (sample every 500ms)
    let lastMouseTrack = Date.now();
    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastMouseTrack > 500) {
        mousePositions.current.push({
          x: e.clientX,
          y: e.clientY,
          time: now,
        });

        // Keep only last 100 positions
        if (mousePositions.current.length > 100) {
          mousePositions.current.shift();
        }

        lastMouseTrack = now;
      }
    };

    // Track form interactions
    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement;
      
      let description = 'Interacción con ';
      if (target.name) {
        description += `campo "${target.name}"`;
      } else if (target.id) {
        description += `campo #${target.id}`;
      } else if (target.placeholder) {
        description += `"${target.placeholder}"`;
      } else {
        description += 'formulario';
      }

      analyticsData.current.interactions++;
      analyticsData.current.events.push({
        type: 'interaction',
        timestamp: new Date().toISOString(),
        data: {
          type: 'input',
          name: target.name,
          id: target.id,
        },
        description,
        color: 'yellow',
        icon: 'ri-hand-coin-line',
      });
      saveToLocalStorage();
    };

    // Track time on page
    const timeInterval = setInterval(() => {
      analyticsData.current.totalTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
      saveToLocalStorage();
    }, 5000);

    // Track page visibility
    const handleVisibilityChange = () => {
      if (document.hidden) {
        analyticsData.current.events.push({
          type: 'page',
          timestamp: new Date().toISOString(),
          data: { action: 'hidden' },
          description: 'Usuario cambió a otra pestaña',
          color: 'rosa',
          icon: 'ri-eye-off-line',
        });
      } else {
        analyticsData.current.events.push({
          type: 'page',
          timestamp: new Date().toISOString(),
          data: { action: 'visible' },
          description: 'Usuario regresó a la pestaña',
          color: 'rosa',
          icon: 'ri-eye-line',
        });
      }
      saveToLocalStorage();
    };

    document.addEventListener('click', handleClick);
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('input', handleInput);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Track referrer
    if (document.referrer) {
      analyticsData.current.events.push({
        type: 'page',
        timestamp: new Date().toISOString(),
        data: { action: 'referrer', url: document.referrer },
        description: `Llegó desde: ${document.referrer}`,
        color: 'rosa',
        icon: 'ri-links-line',
      });
    }

    return () => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('input', handleInput);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(timeInterval);
    };
  }, []);

  const saveToLocalStorage = () => {
    try {
      localStorage.setItem('kalinux_analytics', JSON.stringify(analyticsData.current));
      localStorage.setItem('kalinux_mouse_positions', JSON.stringify(mousePositions.current));
    } catch (e) {
      console.error('Error saving analytics:', e);
    }
  };

  const getAnalyticsReport = () => {
    return {
      ...analyticsData.current,
      mouseHeatmap: mousePositions.current,
      currentTime: Math.floor((Date.now() - startTimeRef.current) / 1000),
    };
  };

  const clearAnalytics = () => {
    analyticsData.current = {
      sessionId: generateSessionId(),
      startTime: new Date().toISOString(),
      events: [],
      pageViews: [window.location.pathname],
      totalTime: 0,
      clicks: 0,
      scrollDepth: 0,
      interactions: 0,
    };
    mousePositions.current = [];
    startTimeRef.current = Date.now();
    lastScrollRef.current = 0;
    saveToLocalStorage();
  };

  return { getAnalyticsReport, clearAnalytics };
};

function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
